import { UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { User, getStateCities, getZipCode, saveUserData } from '../../../services/MyData.service';
import toastify from '../../../utils/toastify';
import { FieldValues, UseFormReset } from 'react-hook-form';
import { UserSchema } from '../MyData.validade';
import {
  SAVE_USER_DATA_QUERY,
  STATE_CITIES_QUERY,
  ZIP_CODE_QUERY
} from '../../../utils/queries/userQueries';

export function useUserFetching({
  reset,
  userData,
  dataNascimento
}: UseUserFetching<UserSchema>): UserFetchingReturn {
  const queryClient = useQueryClient();

  const saveDataMutation = useMutation({
    mutationFn: saveUserData,
    mutationKey: [SAVE_USER_DATA_QUERY],
    onSuccess() {
      toastify.success('Seus dados foram atualizados com sucesso');
      queryClient.invalidateQueries(['user-data']);
    },
    onError() {
      toastify.error(
        'Desculpe, não foi possível alterar os seus dados. Por favor, tente novamente mais tarde'
      );
      reset({ ...userData, dataNascimento });
    }
  });

  function fetchZipCode(zipCode: string) {
    queryClient.fetchQuery({
      queryFn: () => getZipCode(zipCode),
      queryKey: [ZIP_CODE_QUERY, zipCode],
      meta: {
        errorMessage: 'Desculpe, não foi possível buscar pelo CEP nesse momento'
      }
    });
  }

  function fetchCities(state: string) {
    queryClient.fetchQuery({
      queryFn: () => getStateCities(state),
      queryKey: [STATE_CITIES_QUERY, state],
      meta: {
        errorMessage: 'Desculpe, não foi possível buscar pela lista de cidades nesse momento'
      }
    });
  }

  return {
    fetchZipCode,
    fetchCities,
    saveDataMutation
  };
}

interface UseUserFetching<T extends FieldValues> {
  reset: UseFormReset<T>;
  userData: User;
  dataNascimento: string | null;
}

interface UserFetchingReturn {
  saveDataMutation: UseMutationResult<
    void,
    unknown,
    {
      data: Partial<User>;
      type: 'post' | 'put';
    }
  >;
  fetchZipCode: (zipCode: string) => void;
  fetchCities: (state: string) => void;
}
